import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import {
  CopomCalendarResponse,
  CopomEventResponse,
  ExtendedFinancialIndicatorsResponse,
  FocusExpectationResponse,
  FocusReportResponse,
  KeyIndicatorResponse,
  KeyIndicatorsDashboardResponse,
} from '@interfaces/dashboard/responses/key-indicators-dashboard.response';
import { DashboardService } from '@services/http/dashboard.service';
import { SharedModule } from '@shared/shared.module';
import { DashboardIndicatorPreferencesRepository } from '../../services/dashboard-indicator-preferences.repository';
import { Popover } from 'primeng/popover';
import { PopoverModule } from 'primeng/popover';
import { finalize, take } from 'rxjs';

interface KeyIndicatorViewConfig {
  icon: string;
  theme: 'green' | 'purple' | 'blue' | 'orange' | 'pink' | 'neutral';
  caption: string;
  name?: string;
}

interface KeyIndicatorGroup {
  label: string;
  indicators: KeyIndicatorResponse[];
}

interface KeyIndicatorGridItem {
  indicator: KeyIndicatorResponse;
  groupLabel: string | null;
  groupCount: number | null;
}

interface KeyIndicatorSourceDetail {
  label: string;
  value: string;
}

@Component({
  selector: 'app-key-indicators-section',
  imports: [CommonModule, SharedModule, PopoverModule, DragDropModule],
  templateUrl: './key-indicators-section.component.html',
  styleUrls: ['./key-indicators-section.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class KeyIndicatorsSectionComponent {
  readonly additionalIndicators = input<KeyIndicatorResponse[]>([]);
  readonly hiddenIndicatorCodes = input<string[]>([]);
  readonly keyIndicators = signal<KeyIndicatorsDashboardResponse | null>(null);
  readonly isLoading = signal(true);
  readonly isRefreshing = signal(false);
  readonly hasError = signal(false);
  readonly showAllIndicators = signal(false);
  readonly customIndicatorOrder = signal<string[]>([]);
  readonly searchTerm = signal('');
  private activeSourcePopover: Popover | null = null;

  readonly visibleIndicators = computed(() => {
    const indicators = this.filteredIndicators();
    return this.hasSearchTerm() ? indicators : indicators.slice(0, 8);
  });

  readonly listIndicators = computed(() =>
    this.showAllIndicators() || this.hasSearchTerm()
      ? this.filteredIndicators()
      : this.visibleIndicators()
  );

  readonly visibleIndicatorGroups = computed(() => this.groupIndicators(this.visibleIndicators()));

  readonly visibleIndicatorItems = computed(() =>
    this.createIndicatorGridItems(this.visibleIndicatorGroups())
  );

  readonly overflowIndicators = computed(() =>
    this.showAllIndicators() && !this.hasSearchTerm() ? this.filteredIndicators() : []
  );

  readonly overflowIndicatorGroups = computed(() =>
    this.groupIndicators(this.overflowIndicators())
  );

  readonly overflowIndicatorItems = computed(() =>
    this.createIndicatorGridItems(this.overflowIndicatorGroups())
  );

  readonly shouldShowOverflowIndicators = computed(
    () => this.showAllIndicators() && !this.hasSearchTerm() && this.filteredIndicators().length > 8
  );

  readonly hasSearchTerm = computed(() => this.searchTerm().trim().length > 0);

  readonly filteredIndicators = computed(() => {
    const indicators = this.orderedIndicators();
    const search = this.normalizeSearchValue(this.searchTerm());

    if (!search) {
      return indicators;
    }

    return indicators.filter(indicator =>
      this.normalizeSearchValue(this.getSearchableIndicatorText(indicator)).includes(search)
    );
  });

  readonly canToggleIndicators = computed(
    () => !this.hasSearchTerm() && this.orderedIndicators().length > 8
  );

  private readonly preferredIndicatorCodes = [
    'ipca-12m',
    'selic-target',
    'copom-last-minutes',
    'copom-last-statement',
    'copom-next-meeting',
    'copom-next-publication',
    'focus-expectation-ipca',
    'focus-expectation-cambio',
    'focus-expectation-pib',
    'focus-expectation-selic',
    'focus-last-release',
    'focus-next-release',
    'gdp-brazil-4q',
    'industrial-production-monthly-sa',
    'unemployment-rate',
    'focus-ipca-current-year',
  ];

  private readonly indicatorViewConfig: Record<string, KeyIndicatorViewConfig> = {
    cdi: {
      icon: 'pi pi-chart-line',
      theme: 'neutral',
      caption: 'Taxa anualizada',
      name: 'CDI',
    },
    'ipca-month': {
      icon: 'pi pi-percentage',
      theme: 'purple',
      caption: 'Variação mensal',
      name: 'IPCA (Mês)',
    },
    'currency-usd': {
      icon: 'pi pi-dollar',
      theme: 'blue',
      caption: 'Cotação atual',
      name: 'USD/BRL',
    },
    'currency-eur': {
      icon: 'pi pi-euro',
      theme: 'blue',
      caption: 'Cotação atual',
      name: 'EUR/BRL',
    },
    'currency-cny': {
      icon: 'pi pi-money-bill',
      theme: 'blue',
      caption: 'Cotação atual',
      name: 'CNY/BRL',
    },
    'ptax-usd-sale': {
      icon: 'pi pi-dollar',
      theme: 'blue',
      caption: 'Venda PTAX',
      name: 'PTAX USD Venda',
    },
    'ptax-usd-purchase': {
      icon: 'pi pi-dollar',
      theme: 'blue',
      caption: 'Compra PTAX',
      name: 'PTAX USD Compra',
    },
    'ptax-eur-sale': {
      icon: 'pi pi-euro',
      theme: 'blue',
      caption: 'Venda PTAX',
      name: 'PTAX EUR Venda',
    },
    'ptax-eur-purchase': {
      icon: 'pi pi-euro',
      theme: 'blue',
      caption: 'Compra PTAX',
      name: 'PTAX EUR Compra',
    },
    'implicit-ipca': {
      icon: 'pi pi-chart-line',
      theme: 'pink',
      // Deixou de ser DI1 x DAP em 11/08/2026: a curva vem pronta do provedor.
      caption: 'Curva CMA',
      name: 'IPCA Implícito',
    },
    sofr: {
      icon: 'pi pi-globe',
      theme: 'neutral',
      caption: 'Overnight Financing',
      name: 'SOFR',
    },
    'ipca-12m': {
      icon: 'pi pi-chart-line',
      theme: 'green',
      caption: 'Meta: 4,50% e 1,50 p.p.',
      name: 'Inflação (IPCA) - 12M',
    },
    'selic-target': {
      icon: 'pi pi-compass',
      theme: 'purple',
      caption: 'Meta vigente',
      name: 'Taxa Selic',
    },
    'copom-last-minutes': {
      icon: 'pi pi-file',
      theme: 'purple',
      caption: 'Ata publicada',
      name: 'Última ata do Copom',
    },
    'copom-last-statement': {
      icon: 'pi pi-bell',
      theme: 'neutral',
      caption: 'Comunicado publicado',
      name: 'Último comunicado do Copom',
    },
    'copom-next-meeting': {
      icon: 'pi pi-calendar',
      theme: 'blue',
      caption: 'Calendário oficial',
      name: 'Próxima reunião do Copom',
    },
    'copom-next-publication': {
      icon: 'pi pi-calendar-plus',
      theme: 'green',
      caption: 'Ata ou comunicado',
      name: 'Próxima publicação do Copom',
    },
    'gdp-brazil-4q': {
      icon: 'pi pi-file',
      theme: 'blue',
      caption: 'Acumulado 4 trimestres',
      name: 'PIB (Brasil)',
    },
    'industrial-production-monthly-sa': {
      icon: 'pi pi-chart-bar',
      theme: 'orange',
      caption: 'Variação mensal',
      name: 'Produção Industrial',
    },
    'unemployment-rate': {
      icon: 'pi pi-briefcase',
      theme: 'pink',
      caption: 'Taxa de desocupação',
      name: 'Desemprego',
    },
    'focus-ipca-current-year': {
      icon: 'pi pi-bullseye',
      theme: 'neutral',
      caption: 'Expectativa Focus',
      name: 'Focus IPCA esperado',
    },
    'focus-selic-current-year': {
      icon: 'pi pi-bullseye',
      theme: 'purple',
      caption: 'Expectativa Focus',
      name: 'Focus Selic esperado',
    },
    'focus-expectation-ipca': {
      icon: 'pi pi-bullseye',
      theme: 'neutral',
      caption: 'Expectativa Focus',
      name: 'Focus IPCA',
    },
    'focus-expectation-cambio': {
      icon: 'pi pi-dollar',
      theme: 'blue',
      caption: 'Expectativa Focus',
      name: 'Focus Câmbio',
    },
    'focus-expectation-pib': {
      icon: 'pi pi-chart-line',
      theme: 'green',
      caption: 'Expectativa Focus',
      name: 'Focus PIB',
    },
    'focus-expectation-selic': {
      icon: 'pi pi-compass',
      theme: 'purple',
      caption: 'Expectativa Focus',
      name: 'Focus Selic',
    },
    'focus-next-release': {
      icon: 'pi pi-calendar-clock',
      theme: 'orange',
      caption: 'Calendário Focus',
      name: 'Próxima divulgação Focus',
    },
    'focus-last-release': {
      icon: 'pi pi-calendar',
      theme: 'neutral',
      caption: 'Calendário Focus',
      name: 'Última divulgação Focus',
    },
    'igpm-12m': {
      icon: 'pi pi-percentage',
      theme: 'orange',
      caption: 'Acumulado 12 meses',
      name: 'IGP-M - acumulado 12M',
    },
    'ibc-br-monthly-sa': {
      icon: 'pi pi-wave-pulse',
      theme: 'green',
      caption: 'Variação mensal',
      name: 'IBC-Br',
    },
    'primary-result-public-sector-12m': {
      icon: 'pi pi-building-columns',
      theme: 'blue',
      caption: '12 meses',
      name: 'Resultado primário',
    },
    'gross-debt-general-government': {
      icon: 'pi pi-wallet',
      theme: 'neutral',
      caption: '% do PIB',
      name: 'Dívida bruta do governo geral',
    },
    'average-real-monthly-income': {
      icon: 'pi pi-money-bill',
      theme: 'green',
      caption: 'Rendimento real',
      name: 'Rendimento médio mensal real',
    },
    'real-monthly-wage-bill': {
      icon: 'pi pi-users',
      theme: 'purple',
      caption: 'Massa real',
      name: 'Massa de rendimento mensal real',
    },
    'retail-sales-volume-monthly-sa': {
      icon: 'pi pi-shopping-cart',
      theme: 'pink',
      caption: 'Variação mensal',
      name: 'Vendas no varejo',
    },
    'credit-delinquency-free-resources': {
      icon: 'pi pi-exclamation-circle',
      theme: 'orange',
      caption: 'Recursos livres',
      name: 'Inadimplência da carteira de crédito',
    },
  };

  readonly orderedIndicators = computed(() => this.getOrderedIndicators());

  constructor(
    private dashboardService: DashboardService,
    private indicatorPreferencesRepository: DashboardIndicatorPreferencesRepository
  ) {
    this.customIndicatorOrder.set(this.indicatorPreferencesRepository.loadKeyIndicatorOrder());
    this.loadKeyIndicators();
  }

  loadKeyIndicators(forceUpdate = false): void {
    this.hasError.set(false);
    this.isLoading.set(!this.keyIndicators());
    this.isRefreshing.set(forceUpdate);

    this.dashboardService
      .getDashboardKeyIndicators(forceUpdate)
      .pipe(
        take(1),
        finalize(() => {
          this.isLoading.set(false);
          this.isRefreshing.set(false);
        })
      )
      .subscribe({
        next: response => {
          this.keyIndicators.set(response);
          this.showAllIndicators.set(false);
          this.hasError.set(!response);
        },
        error: error => {
          console.warn('Key indicators API error:', error?.message || error);
          this.hasError.set(true);
        },
      });
  }

  toggleIndicators(): void {
    this.showAllIndicators.update(value => !value);
    this.closeActiveSourcePopover();
  }

  dropIndicatorOrder(event: CdkDragDrop<KeyIndicatorResponse[]>): void {
    const orderedIndicators = [...this.orderedIndicators()];
    moveItemInArray(orderedIndicators, event.previousIndex, event.currentIndex);
    const orderedCodes = orderedIndicators.map(indicator => indicator.code);
    this.customIndicatorOrder.set(orderedCodes);
    this.indicatorPreferencesRepository.saveKeyIndicatorOrder(orderedCodes);
  }

  resetIndicatorOrder(): void {
    this.customIndicatorOrder.set([]);
    this.indicatorPreferencesRepository.resetKeyIndicatorOrder();
  }

  updateSearchTerm(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.searchTerm.set(input.value);
    this.showAllIndicators.set(false);
    this.closeActiveSourcePopover();
  }

  clearSearchTerm(): void {
    this.searchTerm.set('');
    this.showAllIndicators.set(false);
    this.closeActiveSourcePopover();
  }

  toggleSourcePopover(event: MouseEvent, popover: Popover): void {
    if (this.activeSourcePopover === popover) {
      popover.hide();
      this.activeSourcePopover = null;
      return;
    }

    this.closeActiveSourcePopover();
    this.activeSourcePopover = popover;
    popover.show(event);
  }

  onSourcePopoverHide(popover: Popover): void {
    if (this.activeSourcePopover === popover) {
      this.activeSourcePopover = null;
    }
  }

  getIndicatorConfig(indicator: KeyIndicatorResponse): KeyIndicatorViewConfig {
    if (indicator.code.startsWith('focus-exchange-rate-')) {
      return {
        icon: 'pi pi-dollar',
        theme: 'blue',
        caption: 'Expectativa Focus',
        name: indicator.name,
      };
    }

    const normalizedName = this.normalizeSearchValue(`${indicator.code} ${indicator.name}`);
    if (indicator.code.startsWith('currency-') || normalizedName.includes('/brl')) {
      return {
        icon: 'pi pi-money-bill',
        theme: 'neutral',
        caption: indicator.unit || 'Cotação oficial',
        name: this.normalizeText(indicator.name),
      };
    }

    return (
      this.indicatorViewConfig[indicator.code] ?? {
        icon: 'pi pi-chart-line',
        theme: 'neutral',
        caption: indicator.unit || 'Indicador oficial',
      }
    );
  }

  getStatusLabel(status: string): string {
    if (status === 'Updated') return 'Atualizado';
    if (status === 'Stale') return 'Cache';
    if (status === 'Failed') return 'Falhou';
    return 'Atualizado';
  }

  getIndicatorTitle(indicator: KeyIndicatorResponse): string {
    const name = this.getIndicatorName(indicator);
    return indicator.period ? `${name} - ${indicator.period}` : name;
  }

  getIndicatorName(indicator: KeyIndicatorResponse): string {
    return this.indicatorViewConfig[indicator.code]?.name ?? this.normalizeText(indicator.name);
  }

  getSourceLabel(indicator: KeyIndicatorResponse): string {
    return this.normalizeText(indicator.source);
  }

  getCompactSourceLabel(indicator: KeyIndicatorResponse): string {
    const source = this.getSourceLabel(indicator);
    const normalizedSource = this.normalizeSearchValue(source);

    if (normalizedSource.includes('focus')) return 'Banco Central/Focus';
    if (normalizedSource.includes('sidra') || normalizedSource.includes('ibge'))
      return 'IBGE/SIDRA';
    if (normalizedSource.includes('ipeadata')) return 'Ipeadata';
    if (normalizedSource.includes('banco central') || normalizedSource.includes('bcb')) {
      return 'Banco Central';
    }
    if (normalizedSource.includes('b3') || normalizedSource.includes('legatus'))
      return 'B3/Legatus';
    if (normalizedSource.includes('federal reserve')) return 'Federal Reserve';

    return source;
  }

  getIndicatorTimeLabel(indicator: KeyIndicatorResponse): string {
    if (indicator.lastSuccessfulUpdateAtGmt) {
      const formatted = this.formatShortDateTime(indicator.lastSuccessfulUpdateAtGmt);
      if (formatted) {
        return formatted;
      }
    }

    return this.getCompactSourceLabel(indicator);
  }

  hasSourceDetails(indicator: KeyIndicatorResponse): boolean {
    return (
      !!indicator.source ||
      !!indicator.period ||
      !!indicator.lastSuccessfulUpdateAtGmt ||
      !!indicator.status ||
      !!indicator.officialPageUrl ||
      !!indicator.apiUrl ||
      !!indicator.metadata?.length
    );
  }

  getSourceDetails(indicator: KeyIndicatorResponse): KeyIndicatorSourceDetail[] {
    const metadata = (indicator.metadata ?? []).filter(item => item.label && item.value);
    const updatedAt = this.formatShortDateTime(indicator.lastSuccessfulUpdateAtGmt);

    return [
      ...(indicator.source ? [{ label: 'Fonte', value: this.getSourceLabel(indicator) }] : []),
      ...(indicator.period ? [{ label: 'Período', value: indicator.period }] : []),
      ...(updatedAt ? [{ label: 'Atualizado em', value: updatedAt }] : []),
      ...(indicator.status
        ? [{ label: 'Status', value: this.getStatusLabel(indicator.status) }]
        : []),
      ...metadata.map(item => ({
        label: this.normalizeText(item.label),
        value: this.normalizeText(item.value),
      })),
    ];
  }

  formatShortDateTime(value: string | null | undefined): string | null {
    if (!value) return null;

    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return null;

    const timeLabel = timePart?.slice(0, 5);
    const shortDate = `${day}/${month}/${year.slice(2)}`;
    return timeLabel ? `${shortDate} ${timeLabel}` : shortDate;
  }

  private getOrderedIndicators(): KeyIndicatorResponse[] {
    const additionalIndicators = this.additionalIndicators();
    const additionalCodes = new Set(additionalIndicators.map(indicator => indicator.code));
    const hiddenCodes = new Set(this.hiddenIndicatorCodes());
    const indicators = (this.keyIndicators()?.indicators ?? []).filter(
      indicator => !additionalCodes.has(indicator.code) && !hiddenCodes.has(indicator.code)
    );
    const copomIndicators = this.getCopomIndicators(this.keyIndicators()?.copom ?? null).filter(
      indicator => !hiddenCodes.has(indicator.code)
    );
    const focusIndicators = this.getFocusIndicators(this.keyIndicators()?.focus ?? null).filter(
      indicator => !hiddenCodes.has(indicator.code)
    );
    const extendedIndicators = this.getExtendedIndicators(
      this.keyIndicators()?.extended ?? null
    ).filter(indicator => !hiddenCodes.has(indicator.code));
    const copomCodes = new Set(copomIndicators.map(indicator => indicator.code));
    const focusCodes = new Set(focusIndicators.map(indicator => indicator.code));
    const extendedCodes = new Set(extendedIndicators.map(indicator => indicator.code));
    const indicatorsWithoutCopomDuplicates = indicators.filter(
      indicator =>
        !copomCodes.has(indicator.code) &&
        !focusCodes.has(indicator.code) &&
        !extendedCodes.has(indicator.code) &&
        !this.isLegacyFocusIndicatorDuplicated(indicator.code, focusIndicators)
    );
    const order = new Map(this.preferredIndicatorCodes.map((code, index) => [code, index]));

    const orderedApiIndicators = [
      ...indicatorsWithoutCopomDuplicates,
      ...copomIndicators,
      ...focusIndicators,
      ...extendedIndicators,
    ].sort((a, b) => {
      const aOrder = order.get(a.code) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = order.get(b.code) ?? Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return a.name.localeCompare(b.name);
    });

    const defaultOrderedIndicators = [...additionalIndicators, ...orderedApiIndicators].sort(
      (a, b) => Number(a.code.startsWith('ptax')) - Number(b.code.startsWith('ptax'))
    );
    const customOrder = this.customIndicatorOrder();
    if (!customOrder.length) {
      return defaultOrderedIndicators;
    }

    const customOrderMap = new Map(customOrder.map((code, index) => [code, index]));

    return [...defaultOrderedIndicators].sort((a, b) => {
      const aOrder = customOrderMap.get(a.code) ?? Number.MAX_SAFE_INTEGER;
      const bOrder = customOrderMap.get(b.code) ?? Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return defaultOrderedIndicators.indexOf(a) - defaultOrderedIndicators.indexOf(b);
    });
  }

  private getCopomIndicators(copom: CopomCalendarResponse | null): KeyIndicatorResponse[] {
    if (!copom) {
      return [];
    }

    const indicators: KeyIndicatorResponse[] = [];
    const lastMinutes = this.createCopomIndicator(
      copom,
      copom.lastMinutes,
      'copom-last-minutes',
      'Última ata do Copom',
      'Última ata do Copom publicada pelo Banco Central do Brasil.',
      copom.lastMinutes?.meetingNumber ? `Reunião ${copom.lastMinutes.meetingNumber}` : 'Publicada'
    );
    const lastStatement = this.createCopomIndicator(
      copom,
      copom.lastStatement,
      'copom-last-statement',
      'Último comunicado do Copom',
      'Último comunicado do Copom publicado pelo Banco Central do Brasil.',
      copom.lastStatement?.meetingNumber
        ? `Reunião ${copom.lastStatement.meetingNumber}`
        : 'Publicado'
    );
    const nextMeeting = this.createCopomIndicator(
      copom,
      copom.nextMeetingsInYear?.[0] ?? null,
      'copom-next-meeting',
      'Próxima reunião do Copom',
      'Próxima reunião do Copom no calendário oficial do Banco Central.',
      this.formatCopomDateLabel(copom.nextMeetingsInYear?.[0]?.date)
    );
    const nextPublication = this.createCopomIndicator(
      copom,
      copom.nextMinutesAndStatementsInYear?.[0] ?? null,
      'copom-next-publication',
      'Próxima publicação do Copom',
      'Próxima publicação oficial de ata ou comunicado do Copom.',
      this.formatCopomDateLabel(copom.nextMinutesAndStatementsInYear?.[0]?.date)
    );

    [lastMinutes, lastStatement, nextMeeting, nextPublication].forEach(indicator => {
      if (indicator) {
        indicators.push(indicator);
      }
    });

    return indicators;
  }

  private getFocusIndicators(focus: FocusReportResponse | null): KeyIndicatorResponse[] {
    if (!focus) {
      return [];
    }

    const expectationIndicators = (focus.expectations ?? [])
      .map(expectation => this.createFocusExpectationIndicator(focus, expectation))
      .filter((indicator): indicator is KeyIndicatorResponse => !!indicator);
    const lastRelease = this.createFocusCalendarIndicator(
      focus,
      focus.pastReleasesInYear?.at(-1) ?? null,
      focus.lastReleaseDate,
      'focus-last-release',
      'Última divulgação Focus',
      'Última divulgação oficial do Boletim Focus no calendário do Banco Central.'
    );
    const nextRelease = this.createFocusReleaseIndicator(
      focus,
      focus.nextReleasesInYear?.[0] ?? null,
      focus.nextReleaseDate,
      'focus-next-release',
      'Próxima divulgação Focus',
      'Próxima divulgação oficial do Boletim Focus no calendário do Banco Central.'
    );

    return [...expectationIndicators, lastRelease, nextRelease].filter(
      (indicator): indicator is KeyIndicatorResponse => !!indicator
    );
  }

  private getExtendedIndicators(
    extended: ExtendedFinancialIndicatorsResponse | null
  ): KeyIndicatorResponse[] {
    if (!extended) {
      return [];
    }

    const focusExchangeRateIndicators = (extended.focusExchangeRate ?? []).map(expectation =>
      this.createExtendedFocusExchangeRateIndicator(extended, expectation)
    );

    return [
      ...this.withIndicatorCategory(extended.bcbAndIpeadata ?? [], 'BCB e Ipeadata'),
      ...this.withIndicatorCategory(extended.ibge ?? [], 'IBGE'),
      ...this.withIndicatorCategory(extended.ipcaByGroup ?? [], 'IPCA por grupo'),
      ...this.withIndicatorCategory(
        extended.industrialProductionByCategory ?? [],
        'Produção industrial por categoria'
      ),
      ...focusExchangeRateIndicators,
    ];
  }

  private withIndicatorCategory(
    indicators: KeyIndicatorResponse[],
    category: string
  ): KeyIndicatorResponse[] {
    return indicators.map(indicator => ({
      ...indicator,
      metadata: [
        ...(indicator.metadata ?? []).filter(item => item.label !== 'Categoria'),
        { label: 'Categoria', value: category },
      ],
    }));
  }

  private createExtendedFocusExchangeRateIndicator(
    extended: ExtendedFinancialIndicatorsResponse,
    expectation: FocusExpectationResponse
  ): KeyIndicatorResponse {
    const collectionDate =
      this.formatCopomDateLabel(expectation.collectionDate) ?? expectation.collectionDate;
    const releaseDate =
      this.formatCopomDateLabel(expectation.releaseDate) ?? expectation.releaseDate;

    return {
      code: `focus-exchange-rate-${expectation.referenceYear}`,
      name: `Focus Câmbio ${expectation.referenceYear}`,
      description: `Mediana da expectativa de câmbio Focus para ${expectation.referenceYear}.`,
      value: expectation.value,
      formattedValue: expectation.formattedValue,
      unit: null,
      period: expectation.referenceYear,
      source: expectation.source,
      apiUrl: expectation.apiUrl,
      officialPageUrl: expectation.officialPageUrl,
      lastSuccessfulUpdateAtGmt: expectation.releaseDate ?? extended.lastSuccessfulUpdateAtGmt,
      status: extended.status,
      errorMessage: extended.errorMessage,
      metadata: [
        { label: 'Categoria', value: 'Focus' },
        { label: 'Ano referência', value: expectation.referenceYear },
        ...(collectionDate ? [{ label: 'Data da pesquisa', value: collectionDate }] : []),
        ...(releaseDate ? [{ label: 'Divulgado em', value: releaseDate }] : []),
        ...(expectation.respondents
          ? [{ label: 'Respondentes', value: String(expectation.respondents) }]
          : []),
      ],
    };
  }

  private createFocusExpectationIndicator(
    focus: FocusReportResponse,
    expectation: FocusExpectationResponse
  ): KeyIndicatorResponse | null {
    const code = this.getFocusExpectationCode(expectation.indicator);
    if (!code) {
      return null;
    }

    const name = this.indicatorViewConfig[code]?.name ?? `Focus ${expectation.indicator}`;
    const collectionDate =
      this.formatCopomDateLabel(expectation.collectionDate) ?? expectation.collectionDate;
    const releaseDate =
      this.formatCopomDateLabel(expectation.releaseDate) ?? expectation.releaseDate;

    return {
      code,
      name,
      description: `Mediana das expectativas de mercado para ${expectation.indicator} no ano de ${expectation.referenceYear}.`,
      value: expectation.value,
      formattedValue: expectation.formattedValue,
      unit: null,
      period: expectation.referenceYear,
      source: expectation.source,
      apiUrl: expectation.apiUrl,
      officialPageUrl: expectation.officialPageUrl,
      lastSuccessfulUpdateAtGmt: expectation.releaseDate ?? focus.lastSuccessfulUpdateAtGmt,
      status: focus.status,
      errorMessage: focus.errorMessage,
      metadata: [
        { label: 'Categoria', value: 'Focus' },
        { label: 'Ano referência', value: expectation.referenceYear },
        ...(collectionDate ? [{ label: 'Data da pesquisa', value: collectionDate }] : []),
        ...(releaseDate ? [{ label: 'Divulgado em', value: releaseDate }] : []),
      ],
    };
  }

  private createFocusReleaseIndicator(
    focus: FocusReportResponse,
    event: CopomEventResponse | null | undefined,
    releaseDate: string | null | undefined,
    code: string,
    name: string,
    description: string
  ): KeyIndicatorResponse | null {
    return this.createFocusCalendarIndicator(focus, event, releaseDate, code, name, description);
  }

  private createFocusCalendarIndicator(
    focus: FocusReportResponse,
    event: CopomEventResponse | null | undefined,
    releaseDate: string | null | undefined,
    code: string,
    name: string,
    description: string
  ): KeyIndicatorResponse | null {
    const date = releaseDate ?? event?.date;
    const formattedDate = this.formatCopomDateLabel(date);
    if (!formattedDate) {
      return null;
    }

    return {
      code,
      name,
      description,
      value: null,
      formattedValue: formattedDate,
      unit: null,
      period: focus.referenceYear,
      source: event?.source ?? 'Banco Central do Brasil/Calendário do BC',
      apiUrl: event?.apiUrl ?? null,
      officialPageUrl: event?.officialPageUrl ?? null,
      lastSuccessfulUpdateAtGmt: focus.lastSuccessfulUpdateAtGmt ?? releaseDate ?? null,
      status: focus.status,
      errorMessage: focus.errorMessage,
      metadata: [
        { label: 'Categoria', value: 'Focus' },
        {
          label: code === 'focus-next-release' ? 'Próxima divulgação' : 'Divulgado em',
          value: formattedDate,
        },
      ],
    };
  }

  private getFocusExpectationCode(indicator: string | null | undefined): string | null {
    const normalized = this.normalizeText(indicator).toLowerCase();

    if (normalized === 'ipca') return 'focus-expectation-ipca';
    if (normalized === 'câmbio' || normalized === 'cambio') return 'focus-expectation-cambio';
    if (normalized === 'pib') return 'focus-expectation-pib';
    if (normalized === 'selic') return 'focus-expectation-selic';

    return null;
  }

  private isLegacyFocusIndicatorDuplicated(
    code: string,
    focusIndicators: KeyIndicatorResponse[]
  ): boolean {
    if (!focusIndicators.length) {
      return false;
    }

    return code === 'focus-ipca-current-year' || code === 'focus-selic-current-year';
  }

  private getSearchableIndicatorText(indicator: KeyIndicatorResponse): string {
    return [
      indicator.code,
      this.getIndicatorName(indicator),
      indicator.name,
      indicator.description,
      indicator.formattedValue,
      indicator.unit,
      indicator.period,
      indicator.source,
      ...(indicator.metadata ?? []).flatMap(item => [item.label, item.value]),
    ]
      .filter(Boolean)
      .join(' ');
  }

  private groupIndicators(indicators: KeyIndicatorResponse[]): KeyIndicatorGroup[] {
    const groups = new Map<string, KeyIndicatorResponse[]>();

    indicators.forEach(indicator => {
      const label = this.getIndicatorCategoryLabel(indicator);
      groups.set(label, [...(groups.get(label) ?? []), indicator]);
    });

    return Array.from(groups.entries()).map(([label, groupIndicators]) => ({
      label,
      indicators: groupIndicators,
    }));
  }

  private createIndicatorGridItems(groups: KeyIndicatorGroup[]): KeyIndicatorGridItem[] {
    return groups.flatMap(group =>
      group.indicators.map((indicator, index) => ({
        indicator,
        groupLabel: index === 0 ? group.label : null,
        groupCount: index === 0 ? group.indicators.length : null,
      }))
    );
  }

  getIndicatorCategoryLabel(indicator: KeyIndicatorResponse): string {
    const metadataCategory = indicator.metadata?.find(item => item.label === 'Categoria')?.value;
    if (metadataCategory) {
      return metadataCategory;
    }

    const code = indicator.code;
    const normalizedName = this.normalizeSearchValue(`${indicator.name} ${indicator.description}`);

    if (code.startsWith('focus-')) return 'Focus';
    if (code.startsWith('copom-')) return 'Copom';
    if (
      code.startsWith('currency-') ||
      code.startsWith('ptax-') ||
      code === 'implicit-ipca' ||
      code === 'sofr'
    ) {
      return 'Mercado e moedas';
    }
    if (
      code === 'cdi' ||
      code.includes('selic') ||
      normalizedName.includes('selic') ||
      normalizedName.includes('taxa de juros')
    ) {
      return 'Juros e política monetária';
    }
    if (
      code.includes('ipca') ||
      code.includes('igpm') ||
      normalizedName.includes('inflacao') ||
      normalizedName.includes('ipca')
    ) {
      return 'Preços';
    }
    if (
      code.includes('industrial') ||
      code.includes('gdp') ||
      code.includes('ibc') ||
      code.includes('retail') ||
      code.includes('unemployment') ||
      normalizedName.includes('producao') ||
      normalizedName.includes('pib') ||
      normalizedName.includes('varejo') ||
      normalizedName.includes('desemprego')
    ) {
      return 'Atividade econômica';
    }
    if (code.includes('credit') || normalizedName.includes('credito')) return 'Crédito';
    if (
      code.includes('debt') ||
      code.includes('primary') ||
      normalizedName.includes('divida') ||
      normalizedName.includes('resultado')
    ) {
      return 'Fiscal';
    }

    return 'Outros indicadores';
  }

  private normalizeSearchValue(value: string | null | undefined): string {
    return (value ?? '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .trim();
  }

  private createCopomIndicator(
    copom: CopomCalendarResponse,
    event: CopomEventResponse | null | undefined,
    code: string,
    name: string,
    description: string,
    formattedValue: string | null
  ): KeyIndicatorResponse | null {
    if (!event) {
      return null;
    }

    return {
      code,
      name,
      description,
      value: null,
      formattedValue,
      unit: null,
      period: this.formatCopomDateLabel(event.date),
      source: event.source,
      apiUrl: event.apiUrl,
      officialPageUrl: event.officialPageUrl,
      lastSuccessfulUpdateAtGmt: copom.lastSuccessfulUpdateAtGmt,
      status: copom.status,
      errorMessage: copom.errorMessage,
    };
  }

  private closeActiveSourcePopover(): void {
    this.activeSourcePopover?.hide();
    this.activeSourcePopover = null;
  }

  private formatCopomDateLabel(value: string | null | undefined): string | null {
    if (!value) return null;

    if (value.includes(' a ')) {
      return value
        .split(' a ')
        .map(item => this.formatCopomDateLabel(item) ?? item)
        .join(' a ');
    }

    const [datePart, timePart] = value.split('T');
    const [year, month, day] = datePart.split('-');
    if (!year || !month || !day) return value;

    const timeLabel = timePart?.slice(0, 5);
    const shortDate = `${day}/${month}/${year.slice(2)}`;
    return timeLabel ? `${shortDate} ${timeLabel}` : shortDate;
  }

  private normalizeText(value: string | null | undefined): string {
    if (!value) return '';

    return value
      .replace(/\bInflacao\b/g, 'Inflação')
      .replace(/\bindice\b/g, 'índice')
      .replace(/\bproducao\b/g, 'produção')
      .replace(/\bProducao\b/g, 'Produção')
      .replace(/\bvariacao\b/g, 'variação')
      .replace(/\bVariacao\b/g, 'Variação')
      .replace(/\bacumulado\b/g, 'acumulado')
      .replace(/\bcredito\b/g, 'crédito')
      .replace(/\bCredito\b/g, 'Crédito')
      .replace(/\bDivida\b/g, 'Dívida')
      .replace(/\bprimario\b/g, 'primário')
      .replace(/\bserie\b/g, 'série')
      .replace(/\bvariavel\b/g, 'variável')
      .replace(/\bprecos\b/g, 'preços')
      .replace(/\bgoverno\b/g, 'governo')
      .replace(/\bcomercio\b/g, 'comércio')
      .replace(/\bultimos\b/g, 'últimos')
      .replace(/\bmensal\b/g, 'mensal')
      .replace(/\bmediana\b/g, 'mediana')
      .replace(/\bano corrente\b/g, 'ano corrente');
  }
}
